import * as React from "react";
import { ActionButton } from "../../../components/Forms/ActionButton";
import { useNavigate } from 'react-router-dom';
import { useGetChallengeCategoriesList } from "../../../queries/challenge";

function SearchHeader({ onFilter, addNewLink }) {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = React.useState("");
    const [activeStatus, setActiveStatus] = React.useState("");
    const [category, setCategory] = React.useState("");
    const [complexity, setComplexity] = React.useState("");
    const [fromDate, setFromDate] = React.useState("");
    const [toDate, setToDate] = React.useState("");

    const { data: categoryData, isPending } = useGetChallengeCategoriesList();

    const handleSearch = (e) => {
        e.preventDefault();

        // If no filters are applied, return all challenges
        if (
            !searchTerm &&
            !activeStatus &&
            !category &&
            !complexity &&
            !fromDate &&
            !toDate
        ) {
            onFilter({
                searchTerm: "",
                activeStatus: "",
                category: "",
                complexity: "",
                fromDate: "",
                toDate: "",
            });
            return;
        }

        // Otherwise, apply the filters
        onFilter({ searchTerm, activeStatus, category, complexity, fromDate, toDate });
    };

    const handleReset = () => {
        // Clear all filter states
        setSearchTerm("");
        setActiveStatus("");
        setCategory("");
        setComplexity("");
        setFromDate("");
        setToDate("");

        // Trigger onFilter with empty values to reset the data
        onFilter({
            searchTerm: "",
            activeStatus: "",
            category: "",
            complexity: "",
            fromDate: "",
            toDate: "",
        });
    };

    if (isPending) {
        return (
            <div className="flex justify-center items-center w-full h-full p-10">
                <h1 className="text-base font-semibold">Loading...</h1>
            </div>
        );
    }

    return (
        <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
            <div className="flex gap-1.5 items-center text-gray-900 max-md:max-w-full">
                <img
                    loading="lazy"
                    src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                    alt=""
                    className="object-contain shrink-0 w-4 aspect-square"
                />
                <h1 className="self-start text-base font-bold">Filters</h1>
            </div>
            <div className="mt-3 flex flex-wrap gap-8 w-full max-md:max-w-full">
                <form
                    className="flex flex-wrap flex-auto gap-2.5 self-start p-2 mt-1 tracking-widest whitespace-nowrap rounded-md border border-solid bg-zinc-300 bg-opacity-0 border-navBtn text-zinc-400"
                    onSubmit={handleSearch}
                >
                    <img
                        loading="lazy"
                        src="https://cdn.builder.io/api/v1/image/assets/TEMP/a945212e3480badae8ec120c2470ee1a05ad62400839168e9007e1ac6f6d6f87?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                        alt=""
                        className="object-contain shrink-0 self-start w-5 aspect-square"
                    />
                    <input
                        type="search"
                        placeholder="Search..."
                        aria-label="Search"
                        className="flex-auto max-md:max-w-full bg-transparent border-none outline-none text-base"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </form>
                <ActionButton
                    variant="reset"
                    text="+ Add New"
                    handleSubmit={() => navigate(addNewLink)}
                />
            </div>
            <div className="flex justify-between mt-3 max-w-full min-h-[65px] w-full max-md:ml-1">
                <div className="flex gap-3 w-full">
                    {/* Status */}
                    <div className="flex-col w-full">
                        <label htmlFor="activeStatus" className="gap-1 self-stretch w-full text-gray-900 text-base">
                            Status
                        </label>
                        <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <select
                                id="activeStatus"
                                className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                value={activeStatus}
                                onChange={(e) => setActiveStatus(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>
                    {/* Category */}
                    <div className="flex-col w-full">
                        <label htmlFor="category" className="gap-1 self-stretch w-full text-gray-900 text-base">
                            Category
                        </label>
                        <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <select
                                id="category"
                                className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                <option value="">All</option>
                                {categoryData?.map((item) => (
                                    <option key={item._id} value={item._id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* Complexity */}
                    <div className="flex-col w-full">
                        <label htmlFor="complexity" className="gap-1 self-stretch w-full text-gray-900 text-base">
                            Complexity
                        </label>
                        <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <select
                                id="complexity"
                                className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                value={complexity}
                                onChange={(e) => setComplexity(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="easy">Easy</option>
                                <option value="moderate">Moderate</option>
                                <option value="hard">Hard</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-between mt-3 max-w-full min-h-[65px] w-full max-md:ml-1 gap-3">
                {/* From Date */}
                <div className="flex-col w-full">
                    <label htmlFor="complexity" className="gap-1 self-stretch w-full text-gray-900 text-base">
                        From Date
                    </label>
                    <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                        <input
                            type="date"
                            id="fromDate"
                            className="flex-1 self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </div>
                </div>
                {/* To Date */}
                <div className="flex-col w-full">
                    <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                        To Date
                    </label>
                    <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                        <input
                            type="date"
                            id="toDate"
                            className="flex-1 self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2.5 mt-5">
                <ActionButton
                    variant="reset"
                    text="Reset"
                    handleSubmit={handleReset}
                />
                <ActionButton variant="submit" text="Search" handleSubmit={handleSearch} />
            </div>
        </div>
    );
}

export default SearchHeader;